import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatDeltaEvent, ChatEvent, ChatMessage } from '@wundo/shared/chat.ts';
import type { ChatActivityStep } from '@wundo/ui';
import { reportChatReceipt } from './chat.ts';
import { currentChatDrawerState } from './chatDrawerState.ts';
import { useChatSocket } from './chatSocket.ts';
import { NO_TURN_ACTIVITY, nextTurnActivity } from './chatTurnActivity.ts';
import { TYPING_TIMEOUT_MS } from './chatTyping.ts';
import { markChatUnread } from './chatUnread.ts';
import { type ChatHistory, useChatHistory } from './useChatHistory.ts';
import { useChatReadReceipt } from './useChatReadReceipt.ts';

const NO_STEPS: readonly ChatActivityStep[] = [];

/**
 * How many sittings keep what arrived in them. Someone moving between
 * conversations in one visit touches a handful; past that the one left alone
 * longest is dropped, so a long session cannot grow without end.
 */
const LIVE_CONVERSATIONS_KEPT = 5;

/** Messages that arrived while the drawer was open, by the sitting they belong to. */
type LiveMessages = ReadonlyMap<string, ChatMessage[]>;

/**
 * A reply as it is being written: the message it is going to be, and what has
 * arrived of it so far. It is replaced by that message the moment it lands,
 * which is the only thing that says the reply is finished.
 */
interface StreamingReply {
  id: string;
  text: string;
}

/**
 * Replies still being written, by the sitting they belong to. A turn can write
 * more than one — a line before it goes off to look something up, then the
 * answer — and each is its own message, so each stays where it was written.
 */
type StreamingReplies = ReadonlyMap<string, readonly StreamingReply[]>;

export interface ChatMessagesOptions {
  drawerOpen: boolean;
  userId: string | undefined;
  /** The conversation on screen, or null where the drawer stands on none. */
  conversationId: string | null;
}

export interface ChatOnScreen {
  /** The conversation on screen: what was read back, then what arrived, once each. */
  messages: ChatMessage[];
  /** How reading it back went, and the way to older pages. */
  history: ChatHistory;
  /** The replies being written in it, minus any already here in full. */
  streaming: readonly StreamingReply[];
  /** Whether Wundo is answering in it before any of its words have arrived. */
  typing: boolean;
  /** Whether a reply is on its way in, in words or in dots. */
  responding: boolean;
  /** The steps Wundo is taking in it for what it says next. */
  activitySteps: readonly ChatActivityStep[];
  /** The steps Wundo took before each reply, by message id. */
  activityPinned: ReadonlyMap<string, readonly ChatActivityStep[]>;
  append: (message: ChatMessage) => void;
  /** Wundo is answering in this sitting now. */
  waitForReply: (conversationId: string) => void;
  /** Wundo is not answering anywhere after all. */
  stopWaiting: () => void;
  /** Drop what arrived, for a drawer that stands on nothing. */
  forget: () => void;
}

/**
 * The messages of the conversation on screen: what was read back of it, what
 * arrives over the chat socket while the drawer is open, and the receipts that
 * say they arrived and were read.
 *
 * The two are kept apart and joined at the end: a reply is pushed the moment
 * it is written and stored just after, so the same message does not carry the
 * same id on both routes.
 */
export function useChatMessages({
  drawerOpen,
  userId,
  conversationId,
}: ChatMessagesOptions): ChatOnScreen {
  const queryClient = useQueryClient();
  const history = useChatHistory(drawerOpen, conversationId);
  const [live, setLive] = useState<LiveMessages>(() => new Map());
  /*
   * Which sitting Wundo is answering, rather than whether it is answering at
   * all: a reply being written for the conversation the user just left must
   * not put dots under the one they moved to.
   */
  const [typingIn, setTypingIn] = useState<string | null>(null);
  /* And, for the same reason again, which sitting a reply is being written in. */
  const [streaming, setStreaming] = useState<StreamingReplies>(() => new Map());
  /* What Wundo is doing for a reply, and did before the ones it began. This session only. */
  const [turnActivity, setTurnActivity] = useState(NO_TURN_ACTIVITY);

  const append = useCallback((message: ChatMessage) => {
    setLive((current) => withLiveMessage(current, message));
  }, []);

  const onEvent = useCallback(
    (event: ChatEvent) => {
      setTurnActivity((current) => nextTurnActivity(current, event));

      if (event.type === 'activity') {
        return;
      }

      if (event.type === 'typing') {
        setTypingIn(event.conversation_id);
        return;
      }

      if (event.type === 'delta') {
        setStreaming((current) => withDelta(current, event));
        return;
      }

      const message = event.message;

      /*
       * Nobody is looking, so the strip holds the news until somebody is. Asked
       * of the drawer's own store rather than of the drawer's props: the socket
       * is subscribed with this handler, and rebuilding it every time the
       * drawer opens would drop the line it is listening on.
       */
      if (message.author === 'wundo' && currentChatDrawerState() !== 'open') {
        markChatUnread();
      }

      /* A notice about files arrives mid-turn; the turn itself is still going. */
      if (message.notice) {
        append(message);
        void reportChatReceipt(message.conversation_id, 'delivered');
        return;
      }

      /* Only the conversation that was waiting stops waiting. */
      setTypingIn((current) => (current === message.conversation_id ? null : current));
      setStreaming((current) => withoutFinished(current, message));
      append(message);

      /* It is in the browser now, whether or not anyone is looking. */
      if (message.author === 'wundo') {
        void reportChatReceipt(message.conversation_id, 'delivered');
        /*
         * The turn may have written on the page behind the drawer — the intake
         * puts a whole preparation there. Nothing tells that page so, and a
         * finished reply is the moment it could have happened. Only a page that
         * is open answers this; one that is not gets read fresh anyway.
         */
        void queryClient.invalidateQueries({ queryKey: ['meetingPage'] });
        void queryClient.invalidateQueries({ queryKey: ['meetingTemplates'] });
      }
    },
    [append, queryClient],
  );

  /*
   * The line was down, so anything said over it was missed. Reading the
   * conversation back is the only way to find out what — and what arrived
   * live is dropped rather than kept alongside it, because a message pushed
   * before it was stored does not carry the id it will be read back under,
   * and the two would show as two. A reply that was half written goes with
   * them: what it got to is on its row, so reading back is what recovers it.
   *
   * Wundo is no longer assumed to be answering: after the refetch either the
   * reply is there, or a fresh typing event says it is still coming.
   */
  const onReconnect = useCallback(() => {
    setLive(new Map());
    setStreaming(new Map());
    setTurnActivity(NO_TURN_ACTIVITY);
    setTypingIn(null);

    void queryClient.invalidateQueries({ queryKey: ['chatMessages'] });
    void queryClient.invalidateQueries({ queryKey: ['chatState'] });
  }, [queryClient]);

  useChatSocket(userId, onEvent, onReconnect);

  /*
   * A reply that never comes — the turn failed, or the socket missed it —
   * would otherwise leave the dots bouncing for the rest of the session. Long
   * enough that a slow turn is never cut short; short enough that a dead one
   * does not look alive.
   */
  useEffect(() => {
    if (!typingIn) {
      return;
    }

    const timer = setTimeout(() => setTypingIn(null), TYPING_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [typingIn]);

  const stopWaiting = useCallback(() => setTypingIn(null), []);

  const forget = useCallback(() => {
    setLive(new Map());
    setStreaming(new Map());
    setTypingIn(null);
  }, []);

  const messages = useMemo(() => {
    const arriving = conversationId ? (live.get(conversationId) ?? []) : [];
    const seen = new Set<string>();

    return [...history.messages, ...arriving].filter((message) => {
      if (seen.has(message.id)) {
        return false;
      }
      seen.add(message.id);
      return true;
    });
  }, [history.messages, live, conversationId]);

  /*
   * What is being written in the sitting on screen, minus anything already
   * here in full: the finished message carries the id its pieces did, so the
   * two would otherwise show as two for as long as one render lasts.
   */
  const streamingReplies = useMemo(() => {
    const arriving = conversationId ? (streaming.get(conversationId) ?? []) : [];

    return arriving.filter(
      (reply) => !messages.some((message) => message.id === reply.id),
    );
  }, [streaming, conversationId, messages]);

  useChatReadReceipt({ drawerOpen, conversationId, lastMessageId: messages.at(-1)?.id });

  /*
   * Dots are for a reply nobody can see yet. Once its words are arriving they
   * say it better than the dots do.
   */
  const typing =
    typingIn !== null && typingIn === conversationId && streamingReplies.length === 0;

  return {
    messages,
    history,
    streaming: streamingReplies,
    typing,
    responding: typing || streamingReplies.length > 0,
    activitySteps: conversationId
      ? (turnActivity.pending.get(conversationId) ?? NO_STEPS)
      : NO_STEPS,
    activityPinned: turnActivity.pinned,
    append,
    waitForReply: setTypingIn,
    stopWaiting,
    forget,
  };
}

/*
 * Appends to whichever sitting the message names, leaving the others as they
 * were: a reply for the conversation the user has just left must not take
 * down what arrived in the one they are reading, and is still theirs to find
 * when they go back to it.
 */
function withLiveMessage(current: LiveMessages, message: ChatMessage): LiveMessages {
  const held = current.get(message.conversation_id) ?? [];

  if (held.some((one) => one.id === message.id)) {
    return current;
  }

  const next = new Map(current);
  /* Re-inserting moves the sitting to the end, so the quietest drops first. */
  next.delete(message.conversation_id);
  next.set(message.conversation_id, [...held, message]);

  if (next.size > LIVE_CONVERSATIONS_KEPT) {
    const quietest = next.keys().next().value;
    if (quietest !== undefined) {
      next.delete(quietest);
    }
  }

  return next;
}

/**
 * Adds to the reply being written in the sitting it names, or starts one. A
 * piece is only ever the next bit of the message it names, so a piece for
 * another message begins one — under whatever was written before it, which
 * stays where it is until the finished messages arrive.
 */
function withDelta(current: StreamingReplies, delta: ChatDeltaEvent): StreamingReplies {
  const held = current.get(delta.conversation_id) ?? [];
  const writing = held.at(-1);
  const next = new Map(current);

  next.set(
    delta.conversation_id,
    writing && writing.id === delta.message_id
      ? [...held.slice(0, -1), { id: writing.id, text: writing.text + delta.text }]
      : [...held, { id: delta.message_id, text: delta.text }],
  );

  return next;
}

/*
 * This message is here in full now, and so is everything written before it.
 * What comes after is still being written, and stays. A message that finishes
 * nothing on the list leaves nothing on it worth keeping.
 */
function withoutFinished(
  current: StreamingReplies,
  message: ChatMessage,
): StreamingReplies {
  const held = current.get(message.conversation_id);

  if (!held) {
    return current;
  }

  const finished = held.findIndex((reply) => reply.id === message.id);
  const writing = finished === -1 ? [] : held.slice(finished + 1);
  const next = new Map(current);

  if (writing.length === 0) {
    next.delete(message.conversation_id);
  } else {
    next.set(message.conversation_id, writing);
  }

  return next;
}
